import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router-dom'
import useAxios from 'axios-hooks'
import _ from 'lodash'
import moment from 'moment'
import * as Icon from 'react-feather'
import { StringParam, NumberParam } from 'use-query-params'

import styled from 'styled-components'
import { toast } from 'react-toastify'
import Breadcrumb from '~/components/Breadcrumb'
import { Col, Hide, Show } from '~/components/commons'
import {
  CustomTable,
  TableRow,
  Content,
  Avatar,
} from '~/components/ecto/CustomTable'
import { useSelector } from 'react-redux'
import {
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import { CallbackAlert } from '~/components/CallbackAlert'
import Pagination from '~/components/ecto/Pagination'

import CustomSwitchGroup from '~/components/CustomSwitchGroup'
import { ButtonCreateMobile } from '~/components/ecto/commons'
import { queries } from '~/constants/mediaQuery'
import CheckBox from '~/components/Checkbox'
import { Alert } from '~/components/Alert'
import { useQueryParamsReplace } from '~/hooks/useQueryParams'
import { differenceInMinutes } from 'date-fns'
import { CLIENT_ADMIN, SUPER_ADMIN } from '~/constants/roles'

function StatusHorizontalFilter({
  horizontal = false,
  value = 'all',
  onChange,
}) {
  const statusOptions = [
    {
      label: 'All projects',
      value: 'all',
      color: '#6236FF',
      icon: 'a',
    },
    {
      label: 'Need verification',
      value: 'needVerification',
      color: '#6236FF',
      icon: 'a',
    },
  ]

  return (
    <CustomSwitchGroup
      options={statusOptions}
      value={value}
      onChange={onChange}
      mobile={horizontal}
      space={50}
      small
    />
  )
}

function DropdownOptions({
  projectId,
  canImportKeywords,
  gmbAccountId,
  onDelete,
  hasAdminPermission,
  openModal,
  createdDate,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importCompleted, setImportCompleted] = useState({})

  const intl = useIntl()

  const [, importLocationAdmin] = useAxios(
    {
      baseURL: process.env.REACT_APP_IMPORTER_API_URL,
      method: 'POST',
    },
    { manual: true }
  )

  const importedRecently = useMemo(() => {
    if (!createdDate) return false
    const difference = differenceInMinutes(new Date(), new Date(createdDate))
    return difference < 0 ? Math.abs(difference) > 175 : difference < 5
  }, [createdDate])

  const titleText = useMemo(() => {
    if (importedRecently) {
      return intl.formatMessage({
        id:
          'Recently imported project, wait for the import process to complete.',
      })
    }

    return ''
  }, [importedRecently, intl])

  function returnImportUrl(type) {
    if (type === 'keywords') {
      return `/admin/locations/keywords/import/${projectId}`
    }

    if (type === 'locations') {
      return `/tasks/locations/import/${gmbAccountId}`
    }

    return `/admin/tasks/${type}/import/${gmbAccountId}`
  }

  const taskImportAdmin = async type => {
    if (importCompleted[type]) return

    setIsImporting(prev => ({ ...prev, [type]: true }))
    importLocationAdmin({
      url: returnImportUrl(type),
    })
      .then(() => {
        setImportCompleted(prev => ({ ...prev, [type]: true }))
      })
      .catch(e => {
        toast.error(`Error on import ${type}`)
        console.error(e)
      })
      .finally(() => {
        setIsImporting(prev => ({ ...prev, [type]: false }))
        openModal(type)
      })
  }

  return (
    <ButtonDropdown
      className="ml-2"
      isOpen={isOpen}
      toggle={() => setIsOpen(prev => !prev)}
    >
      <ButtonToggle size="sm" className="px-1">
        <Icon.MoreHorizontal />
      </ButtonToggle>
      <DropdownMenu title={titleText}>
        <DropdownItem
          onClick={() => taskImportAdmin('locations')}
          disabled={
            isImporting.reviews || importCompleted.reviews || importedRecently
          }
        >
          {importCompleted.locations ? (
            <Icon.Check height={18} />
          ) : (
            <Icon.RefreshCw height={18} />
          )}{' '}
          <FormattedMessage id="Import Locations" />
        </DropdownItem>
        <Show condition={hasAdminPermission}>
          <DropdownItem
            onClick={() => taskImportAdmin('questions')}
            disabled={
              isImporting.reviews || importCompleted.reviews || importedRecently
            }
          >
            {importCompleted.questions ? (
              <Icon.Check height={18} />
            ) : (
              <Icon.HelpCircle height={18} />
            )}{' '}
            <FormattedMessage id="Import Questions" />
          </DropdownItem>
          <DropdownItem
            onClick={() => taskImportAdmin('reviews')}
            disabled={
              isImporting.reviews || importCompleted.reviews || importedRecently
            }
          >
            {importCompleted.reviews ? (
              <Icon.Check height={18} />
            ) : (
              <Icon.Star height={18} />
            )}{' '}
            <FormattedMessage id="Import Reviews" />
          </DropdownItem>
          <DropdownItem
            onClick={() => taskImportAdmin('daily-metrics')}
            disabled={
              isImporting.reviews || importCompleted.reviews || importedRecently
            }
          >
            {importCompleted.insights ? (
              <Icon.Check height={18} />
            ) : (
              <Icon.BarChart2 height={18} />
            )}{' '}
            <FormattedMessage id="Import Insights" />
          </DropdownItem>
          {canImportKeywords && (
            <DropdownItem
              onClick={() => taskImportAdmin('keywords')}
              disabled={
                isImporting.reviews ||
                importCompleted.reviews ||
                importedRecently
              }
            >
              {importCompleted.keywords ? (
                <Icon.Check height={18} />
              ) : (
                <Icon.Key height={18} />
              )}{' '}
              <FormattedMessage id="Import Keywords" />
            </DropdownItem>
          )}

          <DropdownItem onClick={onDelete}>
            <Icon.Trash height={18} />
            <FormattedMessage id="Delete Project" />
          </DropdownItem>
        </Show>
      </DropdownMenu>
    </ButtonDropdown>
  )
}

function ListProjects() {
  const intl = useIntl()
  const [searchInputValue, setSearchInputValue] = useState('')
  const [confirmModal, setConfirmModal] = useState(false)
  const [confirmMultiModal, setConfirmMultiModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState({})
  const [selectedProjects, setSelectedProjects] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [removeGoogle, setRemoveGoogle] = useState(false)
  const profile = useSelector(state => state?.user?.profile)
  const activeProject = useSelector(state => state?.user?.selectedProject)
  const { account, role = null, id: userId } = profile
  const hasAdminPermission = role === SUPER_ADMIN
  const hasClientsPermission = role === CLIENT_ADMIN

  const [alertVisible, setAlertVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const [queryParams, setQueryParams] = useQueryParamsReplace({
    page: NumberParam,
    search: StringParam,
    status: StringParam,
  })

  const location = useLocation()

  function openModal(type) {
    setModalMessage(
      type !== 'delete'
        ? `${_.capitalize(type)} import started!`
        : 'Location deleted successfully!'
    )
    setAlertVisible(true)
  }

  const usersUrl = useMemo(() => {
    if (hasAdminPermission) {
      return '/projects'
    }

    if (hasClientsPermission) {
      return `/accounts/${account?.id}/projects`
    }

    return `/users/${userId}/projects`
  }, [hasAdminPermission, hasClientsPermission, account, userId])

  const [
    { data: { projects = [], count } = {}, loading },
    fetchProjects,
  ] = useAxios(usersUrl, {
    useCache: false,
  })

  useEffect(() => {
    const { search } = queryParams
    setSearchInputValue(search)
  }, [queryParams])

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    fetchProjects({ url: `${usersUrl}?${params.toString()}` })
  }, [location, fetchProjects, usersUrl])

  useEffect(() => {
    if (selectAll) {
      setSelectedProjects(
        projects.map(({ gmbAccountId, id: projectId }) =>
          JSON.stringify({
            gmbAccountId,
            projectId,
          })
        )
      )
    } else {
      setSelectedProjects([])
    }
    // eslint-disable-next-line
  }, [selectAll])

  useEffect(() => {
    if (selectAll)
      setSelectedProjects(
        projects.map(({ gmbAccountId, id: projectId }) =>
          JSON.stringify({
            gmbAccountId,
            projectId,
          })
        )
      )
    // eslint-disable-next-line
  }, [projects])

  const setSearchParams = useCallback(
    _.debounce(function (search) {
      setQueryParams({ page: 1, search })
    }, 500),
    []
  )

  const setFilterParams = status => {
    setQueryParams({ ...queryParams, page: 1, status })
  }

  const headers = useMemo(() => {
    const AllProjectsCheckbox = () => (
      <div className="d-flex justify-content-center align-items-center px-1">
        <CheckBox
          type="checkbox"
          checked={selectAll}
          onChange={() => setSelectAll(prevState => !prevState)}
          className="justify-content-center"
        />
      </div>
    )

    const tableHeader = [
      { name: 'Project', width: '25%', style: { paddingLeft: '30px' } },
      { name: 'Account', center: true },
      { name: 'Expiration', center: true },
      { name: 'Locations', center: true },
      { name: 'Users', center: true },
    ]

    if (hasAdminPermission) {
      tableHeader.unshift({
        name: '_blank',
        elem: AllProjectsCheckbox,
        center: true,
        style: { paddingLeft: '0' },
      })
    }

    if (hasAdminPermission || hasClientsPermission) {
      tableHeader.push({ name: '_blank', center: true, width: '15%' })
    }

    return tableHeader
  }, [hasAdminPermission, hasClientsPermission, selectAll])

  const scrollToTopTable = () => {
    window.scrollTo({ top: 75, behavior: 'smooth' })
  }

  const [, removeLocationAdmin] = useAxios(
    {
      method: 'DELETE',
    },
    { manual: true }
  )

  const removeLocation = async removeFromGoogle => {
    const { gmbAccountId, id: projectId } = selectedProject
    if (!gmbAccountId || !projectId) return

    const index = projects.findIndex(p => p.id === projectId)
    if (index !== -1) projects.splice(index, 1)
    setSelectedProject({})
    try {
      await removeLocationAdmin({
        url: `/admin/locations/account/${gmbAccountId}/project/${projectId}`,
        params: { removeFromGoogle },
      })
      const index = projects.findIndex(p => p.id === projectId)
      if (index !== -1) projects.splice(index, 1)
      setRemoveGoogle(false)
    } catch (e) {
      toast.error('Error on delete location')
      console.error(e)
    } finally {
      setSelectedProject({})
    }
  }

  const removeLocations = async removeFromGoogle => {
    let isInvalid = false
    let selectedProjectsCopy = _.cloneDeep(selectedProjects)

    selectedProjectsCopy = selectedProjectsCopy.map(project =>
      JSON.parse(project)
    )
    selectedProjectsCopy.forEach(({ gmbAccountId, projectId }) => {
      if (!gmbAccountId || !projectId) isInvalid = true
    })
    if (isInvalid) return

    try {
      await removeLocationAdmin({
        url: '/admin/locations/accounts/projects',
        params: { removeFromGoogle },
        data: {
          projects: selectedProjectsCopy,
        },
      })
    } catch (e) {
      toast.error('Error on delete locations')
      console.error(e)
    } finally {
      selectedProjectsCopy.forEach(({ projectId }) => {
        const index = projects.findIndex(p => p.id === projectId)
        if (index !== -1) projects.splice(index, 1)
      })
      setSelectedProjects([])
      setSelectAll(false)
      setRemoveGoogle(false)
    }
  }

  const onChangeChecked = (checked, projectId) => {
    setSelectedProjects(prevState => {
      const set = new Set(prevState)
      const projectObj = projects.find(({ id }) => id === projectId)
      checked
        ? set.add(
            JSON.stringify({
              gmbAccountId: projectObj.gmbAccountId,
              projectId: projectObj.id,
            })
          )
        : set.delete(
            JSON.stringify({
              gmbAccountId: projectObj.gmbAccountId,
              projectId: projectObj.id,
            })
          )
      const arr = Array.from(set)
      return arr
    })
  }

  const getActiveLocations = (locations = []) => {
    return locations.filter(l => !l.deletedAt)?.length
  }

  const getTotalLocations = (locations = []) => {
    return locations.length
  }

  return (
    <>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => setAlertVisible(false)}
        confirmBtnText="Close"
        body={modalMessage}
        title="Success"
      />
      <Col>
        <BreadcrumbContainer>
          <Breadcrumb
            paths={[
              {
                render: () => (
                  <Link className="d-flex align-items-center" to="/">
                    <Icon.Home size={20} />
                  </Link>
                ),
              },
              { text: 'Projects' },
            ]}
          />

          <Show condition={selectedProjects.length && hasAdminPermission}>
            <div className="d-flex align-items-center justify-content-center ml-2 mr-auto">
              <button
                className="d-flex align-items-center justify-content-center gap-1 border-0 bg-danger text-white rounded-pill px-1"
                onClick={() => {
                  if (selectedProjects.length) setConfirmMultiModal(true)
                }}
              >
                <Icon.XCircle height={15} />
                Hard Delete
              </button>
            </div>
          </Show>

          <div className="d-flex align-items-center justify-content-center mb-2 mb-sm-0">
            <StatusHorizontalFilter
              horizontal={true}
              value={queryParams.status}
              onChange={setFilterParams}
            />
            <Show condition={hasAdminPermission && !activeProject.isUnified}>
              <ButtonCreateMobile to="/projects/new">
                <Icon.PlusCircle height={14} className="d-none d-md-block" />
                <Icon.Plus className="d-md-none d-block" />
                <FormattedMessage id="Create project" />
              </ButtonCreateMobile>
            </Show>
          </div>
        </BreadcrumbContainer>

        <CustomTable
          search
          searchPlaceholder="Search project"
          setSearch={search => {
            setSearchParams(search)
            setSearchInputValue(search)
          }}
          searchValue={searchInputValue}
          headers={headers}
          loading={loading}
          id="table-projects"
        >
          {projects.map(project => (
            <TableRow
              key={project.id}
              needVerification={project.needVerification}
            >
              <Show condition={hasAdminPermission}>
                <Content select>
                  <div className="d-flex justify-content-center align-items-center">
                    <CheckBox
                      type="checkbox"
                      checked={selectedProjects.find(selectedProject => {
                        const projectObj = JSON.parse(selectedProject)
                        return projectObj?.projectId === project.id
                      })}
                      onChange={event =>
                        onChangeChecked(event.target.checked, project.id)
                      }
                      className="justify-content-center"
                    />
                  </div>
                </Content>
              </Show>
              <Content text="big">
                <div className="d-flex align-items-center">
                  <Avatar avatar={project.avatar} />
                  {project.isUnified
                    ? project.name.replace('.INTERNAL - ', '')
                    : project.name}
                </div>
              </Content>
              <Content>{project?.account?.name}</Content>
              <Content>
                {project.expirationDate
                  ? moment(project.expirationDate).format('DD/MM/YYYY')
                  : intl.formatMessage({ id: 'No date' })}
              </Content>
              <Content>
                <div className="d-flex align-items-center justify-content-center h-100">
                  <Icon.MapPin className="mr-1" />
                  <Show condition={getTotalLocations(project.locations)}>
                    <p className="m-0">
                      {getActiveLocations(project.locations)}
                    </p>
                    <p className="m-0 ml-1 text-primary">
                      {`(${getTotalLocations(project.locations)})`}
                    </p>
                  </Show>
                  <Hide condition={getTotalLocations(project.locations)}>
                    No data
                  </Hide>
                </div>
              </Content>
              <Content>
                <Icon.Users className="mr-1" />
                {project.users?.length}
              </Content>

              <Content>
                <Show condition={hasAdminPermission}>
                  <Link to={{ pathname: `/projects/${project.id}` }}>
                    <FormattedMessage id="View" />
                  </Link>
                </Show>
                <Show condition={hasAdminPermission || hasClientsPermission}>
                  <DropdownOptions
                    onDelete={() => {
                      setSelectedProject(project)
                      setConfirmModal(true)
                    }}
                    canImportKeywords={project.canImportKeywords}
                    projectId={project.id}
                    gmbAccountId={project.gmbAccountId}
                    hasAdminPermission={hasAdminPermission}
                    openModal={openModal}
                    createdDate={project.createdAt}
                  />
                </Show>
              </Content>
            </TableRow>
          ))}
        </CustomTable>
        <Pagination
          showLimitPerPage
          onPageChange={() => {
            setSelectAll(false)
            setSelectedProjects([])
            scrollToTopTable()
          }}
          onLimitChange={() => {
            if (selectAll)
              setSelectedProjects(
                projects.map(({ gmbAccountId, id: projectId }) =>
                  JSON.stringify({
                    gmbAccountId,
                    projectId,
                  })
                )
              )
            scrollToTopTable()
          }}
          total={count}
        />
        <CallbackAlert
          open={confirmModal}
          setOpen={setConfirmModal}
          warnTitle="Are you sure?"
          warnMessage="Do you wish to remove the following project"
          successTitle="Deleted"
          successMessage="Project successfully deleted"
          errorTitle="Error"
          errorMessage="Failed to delete project"
          callback={removeLocation}
        ></CallbackAlert>

        <CallbackAlert
          open={confirmMultiModal}
          setOpen={setConfirmMultiModal}
          warnTitle="Are you sure?"
          warnMessage="Do you wish to remove the following projects"
          successTitle="Deleted"
          successMessage="Projects successfully deleted"
          errorTitle="Error"
          errorMessage="Failed to delete projects"
          callback={removeLocations}
          callbackParams={removeGoogle}
        >
          <CheckBox
            color="primary"
            label="Delete from Google Business Profile too"
            className="mb-3"
            size="md"
            icon={<Icon.Check color="#fff" size={15} />}
            checked={removeGoogle}
            onChange={() => setRemoveGoogle(prev => !prev)}
          />
        </CallbackAlert>
      </Col>
    </>
  )
}

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;

  @media ${queries.less.xll} {
    flex-direction: column;
    margin-bottom: 10px;
    nav {
      margin-bottom: 20px;
    }
  }
`

const ButtonToggle = styled(DropdownToggle)`
  background-color: transparent !important;
  color: black !important;
  box-shadow: none !important;
  &:hover {
    color: black !important;
  }

  &:focus {
    color: black !important;
  }
`

export default ListProjects
